"use client";

import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useDispatch, useSelector } from "react-redux";
import {
  InputIcon,
  Input,
  Label,
  Select,
  SelectAction,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectValue,
  Switch,
} from "keep-react";
import {
  User,
  IdentificationCard,
  Eye,
  EyeSlash,
} from "phosphor-react";
import { useStyleSchema } from "@components/styleSchema/StyleSchemaHandler";
import {
  closeActivateExistingAccountModal,
  openRegisterModal,
} from "@store/features/userActionsSlice";
import { activateExistingAccount } from "@store/api-helpers/activateAllreadyExistAccountApi";
import { useActivateExistingAccountCompleteMutation } from "@store/api-helpers/activateExistingAccountCompleteApi";
import { setCredentials } from "@store/features/authSlice";
import { ROUTES } from "@constants/routes";
import { CountrySelect } from "@/components/ui/CountrySelect";
import { SteppedBirthdayInput } from "@/components/ui/SteppedBirthdayInput";
import { getCountryByCode, getCountryCodeByGermanName } from "@/lib/constants/countries";
import { Salutation } from "@/models/account-activation-complete.model";
import { log } from "@/lib/utils/logger"
import { toast } from "keep-react";
import { 
  selectBrandString, 
  selectPrivacyLink, 
  selectTermsLink, 
  selectLoyaltyLink 
} from "@store/features/styleSchemaSlice";
import DOMPurify from 'dompurify';

/**
 * Form data structure for step one of the account activation process
 */
type StepOneFormData = {
  /** Customer's first name */
  firstName: string;
  /** Customer's last name */
  lastName: string;
  /** Customer's unique identification number */
  customerNumber: string;
};

/**
 * Extended form data structure for step two of the account activation process
 */
interface FormData extends StepOneFormData {
  // Personal information
  /** Customer's salutation (Mr./Mrs.) */
  salutation: string;
  /** Customer's phone number */
  telefon: string;
  /** Customer's date of birth */
  dateOfBirth: Date;

  // Address information
  /** Street name and house number */
  street: string;
  /** Postal/ZIP code */
  postalCode: string;
  /** City name */
  city: string;
  /** Country code */
  country: string;

  // Account information
  /** Customer's email address */
  email: string;
  /** Email confirmation */
  emailConfirm: string;
  /** Customer's password */
  password: string;
  /** Password confirmation */
  passwordConfirm: string;

  // Consent flags
  /** Marketing consent acceptance */
  marketingAccepted: boolean;
  /** Privacy policy acceptance */
  privacyAccepted: boolean;
}

/**
 * Props for the ActivateAllreadyExistAccountForm component
 */
interface ActivateAllreadyExistAccountFormProps {
  /** Callback function triggered when step changes */
  onStepChange?: (isStepTwo: boolean) => void;
}

/**
 * Form component for activating existing customer accounts through a two-step process
 * 
 * Step 1: Verify customer identity with basic information
 * Step 2: Complete account setup with additional details and consent
 * 
 * Features:
 * - Dynamic URL generation for terms, privacy, and loyalty links
 * - DOMPurify sanitization for security
 * - Brand-specific content customization
 * - Two-step validation process
 */
export function ActivateAllreadyExistAccountForm(
  props: ActivateAllreadyExistAccountFormProps
) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currentSchema } = useStyleSchema();
  const buttonColor = currentSchema.colors?.primary?.hex;
  
  // Redux selectors for dynamic URLs and brand content
  const privacyUrl = useSelector(selectPrivacyLink);
  const termsUrl = useSelector(selectTermsLink);
  const brandString = useSelector(selectBrandString);
  const loyaltyUrl = useSelector(selectLoyaltyLink);

  const [error, setError] = useState("");
  const [disabled, setDisabled] = useState(true);
  const [loading, setLoading] = useState(false);

  // State for two-step form processing
  const [isStepTwo, setIsStepTwo] = useState(false);
  const [customer_id, setCustomerId] = useState<string>("");

  // API hook for the second stage of activation
  const [activateExistingAccountComplete] =
    useActivateExistingAccountCompleteMutation();

  // Notification about status change to parent component
  const { onStepChange } = props;

  /**
   * Pre-processed HTML text for terms and conditions with dynamic URLs
   * Uses DOMPurify for security sanitization
   */
  const termsText = t("register.terms.label", {
    termsLink: `<a class="text-[var(--accent-color)] hover:underline" href="${termsUrl}" target="_blank">`,
    "/termsLink": "</a>",
    privacyLink: `<a class="text-[var(--accent-color)] hover:underline" href="${privacyUrl}" target="_blank">`,
    "/privacyLink": "</a>",
    brandString
  });

  /**
   * Pre-processed HTML text for marketing consent with dynamic loyalty URL
   * Uses DOMPurify for security sanitization
   */
  const marketingText = t("register.marketing.label", {
    privacyLink: `<a class="text-[var(--accent-color)] hover:underline" href="${loyaltyUrl}" target="_blank">`,
    "/privacyLink": "</a>",
  });

  /**
   * Custom setIsStepTwo function that also calls the callback
   */
  const updateStepTwo = (newValue: boolean) => {
    setIsStepTwo(newValue);
    if (onStepChange) {
      onStepChange(newValue);
    }
  };

  // Additional form field states for step 2
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [marketingAccepted, setMarketingAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  // Base schema for step 1
  const baseSchema = z.object({
    firstName: z
      .string()
      .min(1, { message: t("register.validation.firstName.required") || "First name is required" })
      .min(2, { message: t("register.validation.firstName.minLength") }),
    lastName: z
      .string()
      .min(1, { message: t("register.validation.lastName.required") || "Last name is required" })
      .min(2, { message: t("register.validation.lastName.minLength") }),
    customerNumber: z
      .string()
      .min(1, { message: t("account.validation.customer-number-required") || "Customer number is required" })
      .min(5, { message: t("account.validation.customer-number-min-length") }),
  });

  // Extended schema for step 2
  const fullSchema = isStepTwo
    ? baseSchema
        .extend({
          // Personal information
          salutation: z
            .string()
            .min(1, { message: t("register.validation.salutation.required") }),
          telefon: z.string()
            .optional()
            .refine(
              (val) => !val || /^[+]?[0-9\s-()]{8,}$/.test(val),
              {
                message: t("profile.validation.phone_invalid") || "Please enter a valid phone number",
              }
            ),
          dateOfBirth: z
            .date({
              required_error: t("account.validation.dateOfBirth-required"),
              invalid_type_error: t("account.validation.dateOfBirth-invalid"),
            })
            .refine(
              (date) => {
                const currentYear = new Date().getFullYear();
                return (
                  date.getFullYear() >= 1900 && date.getFullYear() <= currentYear
                );
              },
              {
                message: t("account.validation.dateOfBirth-range", {
                  min: 1900,
                  max: new Date().getFullYear(),
                }),
              }
            ),

          // Address information
          street: z
            .string()
            .min(1, { message: t("register.validation.streetAndHouseNumber.required") }),
          postalCode: z
            .string()
            .min(5, { message: t("register.validation.postalCode.minLength") }),
          city: z
            .string()
            .min(1, { message: t("register.validation.city.required") })
            .regex(/^[a-zA-ZäöüÄÖÜß\s\-'.]+$/, { 
              message: t("register.validation.city.invalid") || "Please enter a valid city name (allowed: letters, hyphens, apostrophes and spaces)"
            })
            .transform((val) => val.trim()),
          country: z
            .string()
            .min(1, { message: t("register.validation.country.required") }),

          // Account information
          email: z
            .string()
            .min(1, { message: t("register.validation.email.required") || "Email is required" })
            .email({ message: t("register.validation.email.invalid") }),
          emailConfirm: z
            .string()
            .min(1, { message: t("register.validation.email.required") || "Email is required" })
            .email({ message: t("register.validation.email.invalid") }),
          password: z
            .string()
            .min(1, { message: t("register.validation.password.required") || "Password is required" })
            .min(6, { message: t("register.validation.password.minLength") }),
          passwordConfirm: z
            .string()
            .min(1, { message: t("register.validation.passwordConfirm.required") || "Password confirmation is required" }),

          // Consent flags
          marketingAccepted: z.boolean().optional(),
          privacyAccepted: z.boolean().refine((val) => val === true, {
            message: t("register.validation.privacy.required"),
          }),
        })
        .refine((data) => data.email === data.emailConfirm, {
          message: t("register.validation.email.mismatch"),
          path: ["emailConfirm"],
        })
        .refine((data) => data.password === data.passwordConfirm, {
          message: t("register.validation.password.mismatch"),
          path: ["passwordConfirm"],
        })
    : baseSchema;

  // Use the appropriate schema based on form step
  const schema = fullSchema;

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  // Watch form fields for button activation
  const formValues = watch();

  // Effect to enable/disable button based on form values
  useEffect(() => {
    if (!isStepTwo) {
      // Step 1: Check for firstName, lastName and customerNumber
      const isValid =
        !!formValues.firstName &&
        !!formValues.lastName &&
        !!formValues.customerNumber &&
        !errors.firstName &&
        !errors.lastName &&
        !errors.customerNumber;

      setDisabled(!isValid);
    } else {
      // Step 2: Check all fields and whether terms were accepted
      const requiredFields = [
        "salutation",
        "dateOfBirth",
        "email",
        "emailConfirm",
        "street",
        "postalCode",
        "city",
        "country",
        "password",
        "passwordConfirm",
      ];

      const allFieldsFilled = !requiredFields.some((field) => {
        const value = formValues[field as keyof FormData];
        return !value;
      });

      // Button is only active when all fields are filled and terms are accepted
      setDisabled(
        !allFieldsFilled ||
          !formValues.privacyAccepted ||
          Object.keys(errors).length > 0
      );
    }
  }, [formValues, errors, isStepTwo]);

  // Validation now happens automatically via useEffect

  // Form field handlers
  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("firstName", e.target.value);
  };

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("lastName", e.target.value);
  };

  const handleCustomerNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setValue("customerNumber", e.target.value);
  };

  /**
   * Form submission handler
   * Processes both steps of the account activation process
   */
  const onSubmit = async (data: FormData) => {
    setError("");
    setLoading(true);

    try {
      // If we're still in step 1, first verify customer data
      if (!isStepTwo) {
        const result = await activateExistingAccount({
          firstname: data.firstName,
          lastname: data.lastName,
          customerNumber: data.customerNumber,
        });

        if (result.exists) {
          // Store customer ID for later API call
          if (result.customer_id) {
            setCustomerId(result.customer_id);
          }
          updateStepTwo(true);

          // Pre-fill values from customer data
          if (result.customerData) {
            // Phone
            if (result.customerData.phone) {
              setValue("telefon", result.customerData.phone);
            }

            // Address data
            if (result.customerData.street) {
              setValue("street", result.customerData.street);
            }
            if (result.customerData.zip) {
              setValue("postalCode", result.customerData.zip);
            }
            if (result.customerData.city) {
              setValue("city", result.customerData.city);
            }
            if (result.customerData.country) {
              // Country comes as German name (e.g., "Deutschland"), convert to code
              const countryCode = getCountryCodeByGermanName(result.customerData.country) || "DE";
              setValue("country", countryCode);
            } else {
              setValue("country", "DE");
            }

            // Birthday - Format: MM/dd/yyyy (e.g., "01/01/2007")
            if (result.customerData.birthday) {
              try {
                // Parse MM/dd/yyyy format
                const [month, day, year] = result.customerData.birthday.split('/');
                const birthDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                if (!isNaN(birthDate.getTime())) {
                  setValue("dateOfBirth", birthDate);
                }
              } catch (error) {
                log.error("Error parsing birthday:", error)
              }
            }

            // Email
            if (result.customerData.email) {
              setValue("email", result.customerData.email);
              setValue("emailConfirm", result.customerData.email);
            }

            // Salutation - DB provides numbers (1=Mr, 2=Mrs), form expects i18n keys
            if (result.customerData.salutation) {
              const salutationValue = result.customerData.salutation;
              if (salutationValue === Salutation.Herr) {
                setValue("salutation", t("register.salutations.mr"));
              } else if (salutationValue === Salutation.Frau) {
                setValue("salutation", t("register.salutations.mrs"));
              }
            }
          }
        } else {
          // No matching customer data found
          // Close activation modal and open registration modal
          dispatch(closeActivateExistingAccountModal());
          dispatch(openRegisterModal());
        }
      } else {
        // Transform country code to German name
        const countryName = getCountryByCode(data.country || "DE")?.nameDE || "Deutschland";
        
        // Step 2 - Complete activation through both API calls
        const activationResult = await activateExistingAccountComplete({
          // Authentication data
          email: data.email,
          emailConfirm: data.emailConfirm,
          password: data.password,
          passwordConfirm: data.passwordConfirm,

          // Customer data from step 1
          firstname: data.firstName,
          lastname: data.lastName,
          customerNumber: data.customerNumber,
          dateOfBirth: data.dateOfBirth,
          customer_id: customer_id, // Customer ID from first step

          // Additional customer data from step 2
          salutation: getSalutationEnumValue(data.salutation) || Salutation.Herr,
          telefon: data.telefon || "",
          street: data.street,
          postalCode: data.postalCode,
          city: data.city,
          country: countryName,

          // Consent flags
          termsAccepted: data.privacyAccepted,
          marketingAccepted: data.marketingAccepted,
        }).unwrap();

        // Successful activation
        if (activationResult.success && activationResult.data) {
          // Set authentication in Redux store
          dispatch(
            setCredentials({
              jwt: activationResult.data.jwt,
              user_id: activationResult.data.userId,
              rememberMe: true,
            })
          );

          toast.success(t("account.activation-success"));          
          // Close modal immediately and don't reopen
          dispatch(closeActivateExistingAccountModal());
          
          // Wait briefly and navigate to account page
          setTimeout(() => {
            window.location.href = ROUTES.myAccount;
          }, 1000);
        }
      }
    } catch (err) {
      // Close modal
      setTimeout(() => {
        dispatch(closeActivateExistingAccountModal());
      }, 2000);

      if (err instanceof Error) {
        toast.error(t("api-errors.unknown-error"));
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Simple function for onBlur events
   * Validation happens automatically through React Hook Form
   */
  const handleFieldBlur = () => {
    // Validation happens automatically through React Hook Form
  };

  /**
   * Helper function to toggle password visibility
   */
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  /**
   * Helper function to toggle password confirmation visibility
   */
  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  /**
   * Helper function to convert salutation from i18n key to enum value
   */
  const getSalutationEnumValue = (salutationText: string): Salutation | undefined => {
    if (salutationText === t("register.salutations.mr")) {
      return Salutation.Herr;
    } else if (salutationText === t("register.salutations.mrs")) {
      return Salutation.Frau;
    }
    return undefined;
  };

  /**
   * Helper function to toggle consent flags
   */
  const handlePrivacyToggle = () => {
    const newValue = !privacyAccepted;
    setPrivacyAccepted(newValue);
    setValue("privacyAccepted", newValue, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleMarketingToggle = () => {
    const newValue = !marketingAccepted;
    setMarketingAccepted(newValue);
    setValue("marketingAccepted", newValue);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-4">
        {/* Form title changes based on step */}
        <fieldset>
          <div className="space-y-2">
            <Label
              htmlFor="firstName"
              className="text-[var(--color-slate-600)] font-inter font-medium leading-5 tracking-tight"
            >
              <>
                {t("account.firstname")}
                <span className="text-red-500">*</span>
              </>
            </Label>
            <div className="relative">
              <Input
                id="firstName"
                type="text"
                {...register("firstName")}
                className={
                  errors.firstName
                    ? "border-[var(--color-red-500)] text-[var(--color-red-500)] ps-11 focus:outline-none focus:ring-0 focus-visible:ring-offset-0 focus-visible:border-red-500 focus-visible:ring-0 focus-visible:border-1"
                    : "ps-11 focus:outline-none focus:ring-0 focus-visible:ring-offset-0 focus-visible:border-[var(--color-slate-600)] focus-visible:ring-0 focus-visible:border-1"
                }
                onBlur={handleFieldBlur}
                onChange={handleFirstNameChange}
              />
              <InputIcon>
                <User
                  size={19}
                  className={
                    errors.firstName
                      ? "text-[var(--color-red-500)]"
                      : "text-[#455468]"
                  }
                />
              </InputIcon>
            </div>
            {errors.firstName && (
              <p className="text-red-500 text-sm">{errors.firstName.message}</p>
            )}
          </div>
        </fieldset>

        <fieldset>
          <div className="space-y-2">
            <Label
              htmlFor="lastName"
              className="text-[var(--color-slate-600)] font-inter font-medium leading-5 tracking-tight"
            >
              <>
                {t("account.lastname")}
                <span className="text-red-500">*</span>
              </>
            </Label>
            <div className="relative">
              <Input
                id="lastName"
                type="text"
                {...register("lastName")}
                className={
                  errors.lastName
                    ? "border-[var(--color-red-500)] text-[var(--color-red-500)] ps-11 focus:outline-none focus:ring-0 focus-visible:ring-offset-0 focus-visible:border-red-500 focus-visible:ring-0 focus-visible:border-1"
                    : "ps-11 focus:outline-none focus:ring-0 focus-visible:ring-offset-0 focus-visible:border-[var(--color-slate-600)] focus-visible:ring-0 focus-visible:border-1"
                }
                onBlur={handleFieldBlur}
                onChange={handleLastNameChange}
              />
              <InputIcon>
                <User
                  size={19}
                  className={
                    errors.lastName
                      ? "text-[var(--color-red-500)]"
                      : "text-[#455468]"
                  }
                />
              </InputIcon>
            </div>
            {errors.lastName && (
              <p className="text-red-500 text-sm">{errors.lastName.message}</p>
            )}
          </div>
        </fieldset>

        <fieldset>
          <div className="space-y-2">
            <Label
              htmlFor="customerNumber"
              className="text-[var(--color-slate-600)] font-inter font-medium leading-5 tracking-tight"
            >
              <>
                {t("account.customer-number")}
                <span className="text-red-500">*</span>
              </>
            </Label>
            <div className="relative">
              <Input
                id="customerNumber"
                type="text"
                {...register("customerNumber")}
                className={
                  errors.customerNumber
                    ? "border-[var(--color-red-500)] text-[var(--color-red-500)] ps-11 focus:outline-none focus:ring-0 focus-visible:ring-offset-0 focus-visible:border-red-500 focus-visible:ring-0 focus-visible:border-1"
                    : "ps-11 focus:outline-none focus:ring-0 focus-visible:ring-offset-0 focus-visible:border-[var(--color-slate-600)] focus-visible:ring-0 focus-visible:border-1"
                }
                onBlur={handleFieldBlur}
                onChange={handleCustomerNumberChange}
              />
              <InputIcon>
                <IdentificationCard
                  size={19}
                  className={
                    errors.customerNumber
                      ? "text-[var(--color-red-500)]"
                      : "text-[#455468]"
                  }
                />
              </InputIcon>
            </div>
            {errors.customerNumber && (
              <p className="text-red-500 text-sm">
                {errors.customerNumber.message}
              </p>
            )}
          </div>
        </fieldset>

      </div>

      {error && <p className="text-red-500">{error}</p>}

      {isStepTwo && (
        <>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="salutation">
                  <>
                    {t("register.labels.salutation")}
                    <span className="text-red-500">*</span>
                  </>
                </Label>
                <Controller
                  name="salutation"
                  control={control}
                  render={({ field }) => (
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value);
                        setTimeout(() => field.onBlur(), 100);
                      }}
                      value={field.value as string}
                    >
                      <SelectAction>
                        <SelectValue
                          placeholder={t("register.placeholders.salutation")}
                          className={errors.salutation ? "text-red-500" : ""}
                        />
                      </SelectAction>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value={t("register.salutations.mr")}>
                            {t("register.salutations.mr")}
                          </SelectItem>
                          <SelectItem value={t("register.salutations.mrs")}>
                            {t("register.salutations.mrs")}
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.salutation && (
                  <p className="text-red-500 text-sm">
                    {errors.salutation.message as string}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="telefon">
                  <>
                    {t("register.labels.telefon")}
                  </>
                </Label>
                <Input
                  id="telefon"
                  type="tel"
                  placeholder={t("register.placeholders.telefon")}
                  {...register("telefon")}
                />
                {errors.telefon && (
                  <p className="text-red-500 text-sm">
                    {errors.telefon.message as string}
                  </p>
                )}
              </div>
            </div>
            
            <div className="space-y-2">
              <SteppedBirthdayInput
                name="dateOfBirth"
                control={control}
                label={t("account.birth-date")}
                required={true}
                error={errors.dateOfBirth?.message as string}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4">
          <div className="space-y-2">
                <Label htmlFor="street">
                  <>
                    {t("register.labels.streetAndHouseNumber")}
                    <span className="text-red-500">*</span>
                  </>
                </Label>
                <Input
                  id="street"
                  type="text"
                  placeholder={t("register.placeholders.streetAndHouseNumber")}
                  {...register("street")}
                />
                {errors.street && (
                  <p className="text-red-500 text-sm">
                    {errors.street.message as string}
                  </p>
                )}
              </div>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              

              <div className="space-y-2">
                <Label htmlFor="postalCode">
                  <>
                    {t("register.labels.postalCode")}
                    <span className="text-red-500">*</span>
                  </>
                </Label>
                <Input
                  id="postalCode"
                  type="text"
                  placeholder={t("register.placeholders.postalCode")}
                  {...register("postalCode")}
                />
                {errors.postalCode && (
                  <p className="text-red-500 text-sm">
                    {errors.postalCode.message as string}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="city">
                  <>
                    {t("register.labels.city")}
                    <span className="text-red-500">*</span>
                  </>
                </Label>
                <Input
                  id="city"
                  type="text"
                  placeholder={t("register.placeholders.city")}
                  {...register("city")}
                />
                {errors.city && (
                  <p className="text-red-500 text-sm">
                    {errors.city.message as string}
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <CountrySelect
                name="country"
                control={control}
                label={t("register.labels.country")}
                placeholder={t("register.placeholders.country")}
                required={true}
                defaultValue="DE"
                error={errors.country?.message as string}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">
                  <>
                    {t("register.labels.email")}
                    <span className="text-red-500">*</span>
                  </>
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t("register.placeholders.email")}
                  {...register("email")}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm">
                    {errors.email.message as string}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="emailConfirm">
                  <>
                    {t("register.labels.emailConfirm")}
                    <span className="text-red-500">*</span>
                  </>
                </Label>
                <Input
                  id="emailConfirm"
                  type="email"
                  placeholder={t("register.placeholders.emailConfirm")}
                  {...register("emailConfirm")}
                />
                {errors.emailConfirm && (
                  <p className="text-red-500 text-sm">
                    {errors.emailConfirm.message as string}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">
                  <>
                    {t("register.labels.password")}
                    <span className="text-red-500">*</span>
                  </>
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={t("register.placeholders.password")}
                    {...register("password")}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    {showPassword ? (
                      <EyeSlash size={20} color="#6B7280" />
                    ) : (
                      <Eye size={20} color="#6B7280" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-sm">
                    {errors.password.message as string}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="passwordConfirm">
                  <>
                    {t("register.labels.passwordConfirm")}
                    <span className="text-red-500">*</span>
                  </>
                </Label>
                <div className="relative">
                  <Input
                    id="passwordConfirm"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder={t("register.placeholders.passwordConfirm")}
                    {...register("passwordConfirm")}
                  />
                  <button
                    type="button"
                    onClick={toggleConfirmPasswordVisibility}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    {showConfirmPassword ? (
                      <EyeSlash size={20} color="#6B7280" />
                    ) : (
                      <Eye size={20} color="#6B7280" />
                    )}
                  </button>
                </div>
                {errors.passwordConfirm && (
                  <p className="text-red-500 text-sm">
                    {errors.passwordConfirm.message as string}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-3 mt-6">
            <div className="flex flex-col">
              <div className="flex items-center gap-4">
                <Controller
                  name="privacyAccepted"
                  control={control}
                  render={({ field }) => (
                    <div>
                      <Switch
                        checked={privacyAccepted}
                        onCheckedChange={(checked) => {
                          handlePrivacyToggle();
                          field.onChange(checked);
                        }}
                        className="bg-primary"
                      />
                    </div>
                  )}
                />
                <p
                  className="flex-1 text-sm font-medium text-gray-700"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(termsText) }}
                />
              </div>
              {errors.privacyAccepted && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.privacyAccepted.message as string}
                </p>
              )}
            </div>

            <div className="flex flex-col">
              <div className="flex items-center gap-4">
                <Controller
                  name="marketingAccepted"
                  control={control}
                  render={({ field }) => (
                    <div>
                      <Switch
                        checked={marketingAccepted}
                        onCheckedChange={(checked) => {
                          handleMarketingToggle();
                          field.onChange(checked);
                        }}
                        className="bg-primary"
                      />
                    </div>
                  )}
                />
                <p
                  className="flex-1 text-sm font-medium text-gray-700"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(marketingText)
                  }}
                />
              </div>
            </div>
          </div>
        </>
      )}

      <div className="w-full flex justify-center items-center">
        <button
          type="submit"
          className="px-4 py-2 rounded text-center md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: buttonColor,
            color: "white",
          }}
          disabled={disabled || loading || (isStepTwo && !privacyAccepted)}
        >
          {loading
            ? t("common.loading")
            : isStepTwo
            ? t("account.activate-account")
            : t("auth.continue")}
        </button>
      </div>
      <p className="text-sm text-slate-600">{t("app.requiredFields")}</p>
    </form>
  );
}
